import { useState, useCallback, useEffect } from "react";
import { usePost } from "./usePost";
import { useJobPolling } from "./useJobPolling";
import { useToast } from "../store/ToastProvider";
import { AIModalState } from "../enums/CompassAI";
import {
  AIFileLocation,
  AIExtractionResponse,
  AIFileDownloadRequest,
  AIJobProgress,
} from "../types/compassAITypes";
import { MESSAGES } from "../messages";

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ALLOWED_FILE_TYPE = "application/pdf";

interface UseAIExtractionReturn {
  state: AIModalState;
  uploadedFile: AIFileLocation | null;
  extractionResult: AIExtractionResponse | null;
  errorMessage: string | null;
  progress: AIJobProgress | null;
  uploadPDF: (file: File) => Promise<boolean>;
  startExtraction: () => Promise<void>;
  downloadFile: (path: string) => Promise<string | null>;
  reset: () => void;
  isUploading: boolean;
  isExtracting: boolean;
  isPolling: boolean;
  isComplete: boolean;
  hasError: boolean;
}

export function useAIExtraction(): UseAIExtractionReturn {
  const { addToast } = useToast();

  const [state, setState] = useState<AIModalState>(AIModalState.IDLE);
  const [uploadedFile, setUploadedFile] = useState<AIFileLocation | null>(null);
  const [extractionResult, setExtractionResult] =
    useState<AIExtractionResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { sendData: uploadFile } = usePost<AIFileLocation, { file: File }>();

  const { sendData: getDownloadUrl } = usePost<
    { url: string },
    AIFileDownloadRequest
  >();

  const {
    startJob,
    reset: resetPolling,
    progress,
    result: jobResult,
    errorMessage: jobError,
    isPolling,
    isComplete: jobComplete,
    isFailed: jobFailed,
  } = useJobPolling<AIExtractionResponse>();

  useEffect(() => {
    if (isPolling && state !== AIModalState.POLLING) {
      setState(AIModalState.POLLING);
    }
  }, [isPolling]);

  useEffect(() => {
    if (jobComplete && jobResult) {
      setExtractionResult(jobResult);
      setState(AIModalState.SUCCESS);
    }
  }, [jobComplete, jobResult]);

  useEffect(() => {
    if (jobFailed) {
      setState(AIModalState.ERROR);
      const msg = jobError || MESSAGES.COMPASS_AI.ERRORS.EXTRACTION_FAILED;
      setErrorMessage(msg);
      addToast(MESSAGES.COMPASS_AI.ERRORS.EXTRACTION_FAILED, "error");
    }
  }, [jobFailed]);

  const validateFile = useCallback(
    (file: File): boolean => {
      if (file.type !== ALLOWED_FILE_TYPE) {
        addToast(MESSAGES.COMPASS_AI.PDF_EXTRACTION.INVALID_FILE_TYPE, "error");
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        addToast(MESSAGES.COMPASS_AI.PDF_EXTRACTION.FILE_TOO_LARGE, "error");
        return false;
      }
      return true;
    },
    [addToast],
  );

  const uploadPDF = useCallback(
    async (file: File): Promise<boolean> => {
      if (!validateFile(file)) {
        return false;
      }

      setState(AIModalState.UPLOADING);
      setErrorMessage(null);

      const result = await uploadFile(
        "UPLOAD_ENGAGEMENT_PDF",
        { file },
        {},
        true, // isFileUpload
      );

      if (result) {
        setUploadedFile(result);
        setState(AIModalState.UPLOADED);
        return true;
      } else {
        setState(AIModalState.ERROR);
        setErrorMessage(MESSAGES.COMPASS_AI.ERRORS.UPLOAD_FAILED);
        addToast(MESSAGES.COMPASS_AI.ERRORS.UPLOAD_FAILED, "error");
        return false;
      }
    },
    [uploadFile, validateFile, addToast],
  );

  const startExtraction = useCallback(async (): Promise<void> => {
    if (!uploadedFile?.file) {
      setErrorMessage("No file uploaded");
      return;
    }

    setState(AIModalState.EXTRACTING);
    setErrorMessage(null);

    await startJob("AI_EXTRACT_ENGAGEMENTS", {
      pdf_filename: uploadedFile.file,
    } as unknown as Record<string, unknown>);
  }, [uploadedFile, startJob]);

  const downloadFile = useCallback(
    async (path: string): Promise<string | null> => {
      const result = await getDownloadUrl("FILE_DOWNLOAD", { path });

      if (result?.url) {
        return result.url;
      } else {
        addToast(MESSAGES.COMPASS_AI.ERRORS.DOWNLOAD_FAILED, "error");
        return null;
      }
    },
    [getDownloadUrl, addToast],
  );

  const reset = useCallback((): void => {
    setState(AIModalState.IDLE);
    setUploadedFile(null);
    setExtractionResult(null);
    setErrorMessage(null);
    resetPolling();
  }, [resetPolling]);

  return {
    state,
    uploadedFile,
    extractionResult,
    errorMessage,
    progress,
    uploadPDF,
    startExtraction,
    downloadFile,
    reset,
    isUploading: state === AIModalState.UPLOADING,
    isExtracting:
      state === AIModalState.EXTRACTING || state === AIModalState.POLLING,
    isPolling: state === AIModalState.POLLING,
    isComplete: state === AIModalState.SUCCESS,
    hasError: state === AIModalState.ERROR,
  };
}
